import * as React from 'react';
import { designChangeReviewSchema } from '@/features/drbfm/schemas/designChangeReview.schema';
import type {
  DesignChange,
  DesignChangeReviewStatus,
  UpdateDesignChangeReviewRequest,
} from '../../../../types/drbfm';

type DraftField = 'oldSpecification' | 'newSpecification' | 'changeDescription';
type Draft = Record<DraftField, string>;

const FIELDS: DraftField[] = ['oldSpecification', 'newSpecification', 'changeDescription'];

const storedValues = (change: DesignChange): Draft => ({
  oldSpecification: change.oldSpecification || '',
  newSpecification: change.newSpecification || '',
  changeDescription: change.changeDescription || '',
});

const describes = (text: string) =>
  designChangeReviewSchema.safeParse({ changeDescription: text }).success;

const integrityIssues = (change: DesignChange): string[] => {
  const issues = change.changeMetadata?.validation_issues;
  return Array.isArray(issues) ? issues.map(String) : [];
};

// Only these reasons can be confirmed, and only for a pair quoted verbatim on both sides (#1525 Q5).
const COMPARABILITY_REASONS = new Set(['different_basis', 'unit_unconvertible', 'attribute_unverified']);

const comparability = (change: DesignChange, issues: string[]) => {
  const metadata = change.changeMetadata ?? {};
  const quote = (metadata.source_quote ?? {}) as { old?: unknown; new?: unknown };
  const sources = (metadata.source_artifact_ids ?? {}) as { old?: unknown; new?: unknown };
  const confirmable = issues.length > 0
    && issues.every((issue) => COMPARABILITY_REASONS.has(issue))
    && Boolean(quote.old && quote.new && sources.old && sources.new);
  const record = metadata.comparability_confirmation as { rationale?: unknown } | undefined;
  return {
    offered: confirmable && !record,
    confirmed: confirmable && Boolean(record),
    rationale: record ? String(record.rationale ?? '') : '',
  };
};

const MEANINGFUL = /[\p{L}\p{N}]/u;

// The form keeps the rationale until the server's confirmation arrives, so a failed request loses nothing.
export const useComparabilityConfirmation = (
  changeId: string,
  confirmed: boolean,
  onConfirm?: (designChangeId: string, rationale: string) => unknown,
) => {
  const [open, setOpen] = React.useState(false);
  const [rationale, setRationale] = React.useState('');
  React.useEffect(() => {
    if (confirmed) {
      setOpen(false);
      setRationale('');
    }
  }, [confirmed]);
  const canSubmit = MEANINGFUL.test(rationale);
  const submit = () => {
    if (canSubmit && onConfirm) onConfirm(changeId, rationale.trim());
  };
  return { open, setOpen, rationale, setRationale, submit, canSubmit };
};

// A decision carries the pending edits in the same request; an edit alone never adopts a candidate.
export const useDesignChangeReviewDraft = (
  change: DesignChange,
  onReviewChange: (designChangeId: string, request: UpdateDesignChangeReviewRequest) => unknown,
) => {
  const stored = storedValues(change);
  const [draft, setDraft] = React.useState<Draft>(stored);
  const synced = React.useRef<Draft>(stored);
  // What the last decision sent, until the server's refresh arrives or the request fails.
  const decided = React.useRef<{ status: DesignChangeReviewStatus; values: Partial<Draft> } | null>(null);

  const { oldSpecification, newSpecification, changeDescription, reviewStatus } = change;
  React.useEffect(() => {
    decided.current = null;
  }, [oldSpecification, newSpecification, changeDescription, reviewStatus]);
  // A server value replaces a field only while the reviewer has not typed in it since the last sync.
  React.useEffect(() => {
    const next = storedValues({ oldSpecification, newSpecification, changeDescription } as DesignChange);
    const changed = FIELDS.filter((field) => next[field] !== synced.current[field]);
    if (!changed.length) return;
    const previous = synced.current;
    synced.current = next;
    setDraft((current) => ({
      ...current,
      ...Object.fromEntries(changed
        .filter((field) => current[field] === previous[field])
        .map((field) => [field, next[field]])),
    }));
  }, [oldSpecification, newSpecification, changeDescription]);

  // Adoption requires a description, so a blank one is only sent with a non-adopting status.
  const pendingEdits = (fields: DraftField[], adopting: boolean) => Object.fromEntries(
    fields
      .filter((field) => draft[field] !== stored[field])
      .filter((field) => field !== 'changeDescription' || !adopting || describes(draft.changeDescription))
      .map((field) => [field, draft[field]]),
  ) as Partial<Draft>;

  const edit = (field: DraftField, value: string) => setDraft((current) => ({ ...current, [field]: value }));

  const saveEdit = (field: DraftField) => {
    const current = decided.current?.status ?? reviewStatus ?? 'ai_candidate';
    const status = current === 'accepted' ? 'modified' : current;
    const edits = pendingEdits([field], status === 'modified');
    if (!Object.keys(edits).length || edits[field] === decided.current?.values[field]) return;
    onReviewChange(change.id, { reviewStatus: status, ...edits });
  };

  const decide = (status: DesignChangeReviewStatus) => {
    const edits = pendingEdits(FIELDS, status === 'accepted');
    const sent = status === 'accepted' && Object.keys(edits).length > 0 ? 'modified' : status;
    const sentDecision = { status: sent, values: edits };
    decided.current = sentDecision;
    // A failed save leaves the stored status, so later edits must not carry the unsaved decision.
    Promise.resolve(onReviewChange(change.id, { reviewStatus: sent, ...edits })).then((saved) => {
      if (saved === false && decided.current === sentDecision) decided.current = null;
    });
  };

  const issues = integrityIssues(change);
  const confirmation = comparability(change, issues);
  return {
    draft,
    edit,
    saveEdit,
    decide,
    incomplete: !describes(draft.changeDescription),
    issues,
    confirmation,
    held: issues.length > 0 && !confirmation.confirmed,
  };
};
